;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { create, type StoreApi } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { createStorage } from "./storage";

// Allow any object type for state
type StoreState = object;

// Allow any object type for actions
type StoreActions = object;

type Store<T extends StoreState, A extends StoreActions> = T &
	A & {
		_hasHydrated: boolean;
		setHasHydrated: (state: boolean) => void;
	};

type PersistOptions<T extends StoreState> = {
	enabled: boolean;
	type?: "local" | "session";
	version?: number;
	migrate?: (persistedState: unknown, version: number) => T;
};

type CreateStoreOptions<T extends StoreState, A extends StoreActions> = {
	name: string;
	initialState: T;
	actions: (
		set: StoreApi<Store<T, A>>["setState"],
		get: StoreApi<Store<T, A>>["getState"],
	) => A;
	persist?: PersistOptions<T>;
};

export function createStore<T extends StoreState, A extends StoreActions>({
	name,
	initialState,
	actions,
	persist: persistOptions,
}: CreateStoreOptions<T, A>) {
	const store = (
		set: StoreApi<Store<T, A>>["setState"],
		get: StoreApi<Store<T, A>>["getState"],
	) => {
		const state = { ...initialState };
		const storeActions = actions(set, get);
		return {
			...state,
			...storeActions,
			_hasHydrated: false,
			setHasHydrated: (state: boolean) => {
				set({ _hasHydrated: state } as Partial<Store<T, A>>);
			},
		};
	};

	// Apply persist if enabled
	if (persistOptions?.enabled) {
		return create<Store<T, A>>()(
			devtools(
				persist(store, {
					migrate: persistOptions.migrate,
					name,
					onRehydrateStorage: (state) => {
						return () => {
							state.setHasHydrated(true);
						};
					},
					partialize: (state) => {
						const result: Partial<T> = {} as Partial<T>;
						for (const key in state) {
							if (key in initialState) {
								result[key as keyof T] = state[key as keyof T];
							}
						}
						return result;
					},
					storage: createJSONStorage(() =>
						createStorage(persistOptions.type ?? "local"),
					),
					version: persistOptions.version ?? 1,
				}),
				{ name },
			),
		);
	}

	// Apply devtools
	return create<Store<T, A>>()(devtools(store, { name }));
}
